import moment from 'moment';
import {
  BookingTransactionStatus,
  BuyerInvoiceStatusGroup,
  SupplierInvoiceStatusGroup,
} from '../constants/booking-transaction-constants';
import { BookingTransactionTypeLabel } from '../constants/securspace-constants';
import type { AssistantAudience } from './assistantCapabilities';
import type { KnowledgeLink } from './knowledgeRegistry';
import { displayDate, enumLabel, shiftedDay } from './lookupFormat';
import type { InvoiceLookupRow } from './requests/invoice-lookup-requests';

/**
 * What an invoice's status means, said the way each audience needs to hear it.
 *
 * One raw status is three different facts. `SECURSPACE_PAYOUT_SUCCEEDED` is
 * "Complete" to the buyer — they paid — while to the supplier it is "Payout
 * Ready", money not yet in their bank; staff need the raw state to work out
 * which step stalled. The buyer and supplier wordings come from the status
 * groups the invoice pages already show, so the assistant and the page agree.
 *
 * The explanations never guess at a cause the record does not hold. Where a
 * processor message or a status note exists it is quoted; where none does, the
 * answer says what the status means and stops.
 */

type Explain = (row: InvoiceLookupRow) => string;

const REFUND_TYPE = /_REFUND$/;

/** True for an invoice that returns money to the buyer rather than charging them. */
export const isRefund = (row: InvoiceLookupRow): boolean => REFUND_TYPE.test(row.transactionType ?? '');

/** Every transaction type {@link isRefund} treats as a refund, for filtering a search by type. */
export const REFUND_TRANSACTION_TYPES: readonly string[] = Object.keys(BookingTransactionTypeLabel).filter((type) =>
  REFUND_TYPE.test(type),
);

/** "Booking Payment", "Overage Payment", "Cancellation Refund" … */
export const invoiceTypeLabel = (row: InvoiceLookupRow): string =>
  (BookingTransactionTypeLabel as Record<string, string>)[row.transactionType ?? ''] ??
  (enumLabel(row.transactionType) || 'Invoice');

/** The buyer-facing status group, from the row or derived from its raw status. */
export const buyerGroupOf = (row: InvoiceLookupRow): string =>
  row.buyerTransactionStatus ?? BookingTransactionStatus[row.status ?? '']?.buyerInvoiceStatusGroup.value ?? '';

/** The supplier-facing status group, from the row or derived from its raw status. */
export const supplierGroupOf = (row: InvoiceLookupRow): string =>
  row.supplierTransactionStatus ??
  BookingTransactionStatus[row.status ?? '']?.supplierInvoiceStatusGroup.value ??
  '';

/** The status label the audience's own invoice page shows for this row. */
export const statusLabelFor = (row: InvoiceLookupRow, audience: AssistantAudience): string => {
  if (audience === 'buyer') {
    const group = buyerGroupOf(row);
    return BuyerInvoiceStatusGroup[group]?.label ?? enumLabel(group);
  }
  if (audience === 'supplier') {
    const group = supplierGroupOf(row);
    return SupplierInvoiceStatusGroup[group]?.label ?? enumLabel(group);
  }
  return BookingTransactionStatus[row.status ?? '']?.label ?? enumLabel(row.status);
};

const on = (date?: string): string => (displayDate(date) ? ` on ${displayDate(date)}` : '');
const since = (date?: string): string => (displayDate(date) ? ` (started ${displayDate(date)})` : '');
const quoted = (text?: string): string => (text?.trim() ? ` \u2014 \u201c${text.trim()}\u201d` : '');

const BUYER: Record<string, Explain> = {
  BOOKING_DECLINED: () => 'The booking was declined, so this will not be charged.',
  CHARGE_PENDING: (row) =>
    isRefund(row) ? 'The refund has not been issued yet.' : 'It has not been charged yet.',
  INCOMPLETE: () => 'The booking was never completed, so this will not be charged.',
  NEEDS_CUSTOMER_APPROVAL: () =>
    'It is on hold awaiting customer approval, so it has not been charged.',
  NOT_APPROVED: () => 'The payment has not been submitted yet.',
  REFUND_REQUESTED: () => 'A refund has been requested on it and is being reviewed.',
  REFUND_DENIED: () => 'A refund was requested on it and denied.',
  MANUAL_PAYMENT: () =>
    'It is paid outside SecurSpace (a manual payment), so it is not charged to your payment method.',
  PAYMENT_FAILED: (row) =>
    `The payment failed${quoted(row.paymentMessage)}. Update the payment method on the booking ` +
    'and resubmit the payment from **Bookings \u2192 Payment Failed**, or contact SecurSpace support.',
  PAYMENT_PENDING: (row) =>
    isRefund(row)
      ? `The refund is being processed${since(row.paymentCreatedOn)}.`
      : `The payment is being processed${since(row.paymentCreatedOn)}.`,
  PAYMENT_SUCCEEDED: (row) =>
    isRefund(row)
      ? `The refund has been processed${on(row.paymentCompletedOn)}.`
      : `It is paid${on(row.paymentCompletedOn)}${
          row.paymentMethodDescription ? ` with ${row.paymentMethodDescription}` : ''
        }.`,
  UNCOLLECTIBLE: () => 'It has been marked uncollectible.',
  CANCELLED: () => 'It was cancelled, so it will not be charged.',
  DECLINED: () => 'The charge was declined and has to be resolved manually \u2014 contact SecurSpace support.',
};

const SUPPLIER: Record<string, Explain> = {
  BOOKING_DECLINED: () => 'The booking was declined, so this will not be charged or paid out.',
  CHARGE_ON_HOLD: () =>
    'The charge is on hold, so the customer has not been charged and there is nothing to pay out yet.',
  CHARGE_PENDING: () => 'The customer has not been charged for it yet, so it is not ready for payout.',
  INCOMPLETE: () => 'The customer never completed the booking, so this will not be charged or paid out.',
  REFUND_REQUESTED: () => 'A refund has been requested on it and is being reviewed.',
  REFUND_DENIED: () => 'A refund was requested on it and denied.',
  MANUAL_PAYMENT: () => 'It is paid outside SecurSpace, so SecurSpace does not pay it out.',
  NOT_APPROVED: () =>
    'The customer\u2019s payment has not been submitted yet, so it is not ready for payout.',
  PAYMENT_FAILED: () =>
    'The customer\u2019s payment failed, so there is nothing to pay out until it is collected.',
  PAYMENT_PENDING: () =>
    'The customer\u2019s payment is still settling, so it is not ready for payout yet.',
  PAYOUT_FAILED: () =>
    'The payout to you failed. Check your payout bank account under **Account \u2192 Payout Bank ' +
    'Account**, or contact SecurSpace support.',
  PAYOUT_ON_HOLD: () => 'The payout is on hold because of an active dispute.',
  PAYOUT_PENDING: (row) => `The payout to you is on its way${since(row.payoutCreatedOn)}.`,
  PAYOUT_READY: () =>
    'The customer has paid, so it is ready for payout and will go out with an upcoming payout.',
  PAYOUT_SUCCEEDED: (row) => `It has been paid out to you${on(row.payoutCompletedOn)}.`,
  UNCOLLECTIBLE: () => 'It has been marked uncollectible, so it will not be paid out.',
  CANCELLED: () => 'It was cancelled, so it will not be charged or paid out.',
  DECLINED: () => 'The charge was declined and has to be resolved manually by SecurSpace.',
};

/**
 * Where the supplier's money is, for staff, keyed by raw status. Only the
 * states where "why hasn't the supplier been paid" has a non-obvious answer;
 * the rest are explained by the status description itself.
 */
const NOT_YET_CHARGED = 'Nothing can be paid out until the buyer is charged.';
const STILL_SETTLING =
  'The buyer\u2019s payment has not finished settling into SecurSpace; it becomes payable at ' +
  '**SecurSpace Payout Succeeded**.';
const ADMIN_PAYOUT: Record<string, Explain> = {
  BOOKING_INCOMPLETE: () => NOT_YET_CHARGED,
  CHARGE_PENDING: () => NOT_YET_CHARGED,
  PAYMENT_NOT_SUBMITTED: () => NOT_YET_CHARGED,
  PAYMENT_AUTHORIZED: () => NOT_YET_CHARGED,
  PAYMENT_PENDING: () => STILL_SETTLING,
  PAYMENT_SUCCEEDED: () => STILL_SETTLING,
  SECURSPACE_PAYOUT_PENDING: () => STILL_SETTLING,
  SECURSPACE_PAYOUT_SUCCEEDED: (row) =>
    row.supplierPayoutOnHold
      ? 'The funds have reached SecurSpace, but the payout is **on hold** for this invoice.'
      : 'The funds have reached SecurSpace, so it is ready for payout.',
  SUPPLIER_PAYOUT_INITIATING: (row) => `The payout is being created${since(row.payoutCreatedOn)}.`,
  SUPPLIER_PAYOUT_PENDING: (row) => `The payout is in transit${since(row.payoutCreatedOn)}.`,
  SUPPLIER_PAYOUT_SUCCEEDED: (row) => `The supplier was paid out${on(row.payoutCompletedOn)}.`,
};

/**
 * One or two sentences on where the invoice stands, for the audience asking.
 * Refunds are described by their type and status only: the payout narrative
 * is about money reaching a supplier, which a refund does not do.
 */
export const explainInvoice = (row: InvoiceLookupRow, audience: AssistantAudience): string => {
  if (audience === 'buyer') {
    const explain = BUYER[buyerGroupOf(row)];
    return explain ? explain(row) : `Its status is **${statusLabelFor(row, audience)}**.`;
  }

  if (audience === 'supplier') {
    if (isRefund(row)) return `Its status is **${statusLabelFor(row, audience)}**.`;
    const group = supplierGroupOf(row);
    const explain = SUPPLIER[group];
    const sentence = explain ? explain(row) : `Its status is **${statusLabelFor(row, audience)}**.`;
    const held =
      row.supplierPayoutOnHold && group !== 'PAYOUT_ON_HOLD' && group !== 'PAYOUT_SUCCEEDED'
        ? ' SecurSpace has also put the payout for this invoice on hold.'
        : '';
    return sentence + held;
  }

  const status = BookingTransactionStatus[row.status ?? ''];
  const described = status
    ? `Status: **${status.label}** \u2014 ${status.description.replace(/\.?$/, '.')}`
    : `Status: **${enumLabel(row.status) || 'unknown'}**.`;
  const payout = !isRefund(row) ? ADMIN_PAYOUT[row.status ?? '']?.(row) : undefined;
  // Already said by the SecurSpace-payout sentence, and moot once the supplier is paid.
  const held =
    row.supplierPayoutOnHold &&
    row.status !== 'SECURSPACE_PAYOUT_SUCCEEDED' &&
    supplierGroupOf(row) !== 'PAYOUT_SUCCEEDED'
      ? ' The supplier payout is flagged **on hold** for this invoice.'
      : '';
  return `${described}${payout ? ` ${payout}` : ''}${held}`;
};

/** The days a link's records were created on, oldest and newest. */
export interface InvoiceLinkWindow {
  from?: string | null;
  to?: string | null;
}

/**
 * The audience's invoice page filtered to one invoice or one booking's invoices.
 *
 * Each page filters on the day an invoice was created and opens on a recent
 * window by default, which would hide an older invoice behind an empty list, so
 * the link starts the window on the oldest invoice's day. Only the supplier page
 * also ends its window early by default — a month after its start — so only its
 * link carries an end. The buyer page names its filters after the API fields
 * (`transactionNumber`, `orderNumber`); the admin and supplier pages do not.
 */
export const invoicePageLink = (
  audience: AssistantAudience,
  filter: { invoiceNumber: string } | { bookingNumber: string },
  created: InvoiceLinkWindow = {},
): KnowledgeLink => {
  const isInvoice = 'invoiceNumber' in filter;
  const params = new URLSearchParams();
  const label = isInvoice ? 'Open this invoice' : 'Open these invoices';
  const startDate = shiftedDay(created.from, -1);
  const endDate = shiftedDay(created.to ?? moment().format('MM/DD/YYYY'), 1);

  isInvoice ? params.set(audience === 'buyer' ? 'transactionNumber' : 'invoiceNumber', filter.invoiceNumber)
    : params.set(audience === 'buyer' ? 'orderNumber' : 'bookingNumber', filter.bookingNumber);
  if (startDate) params.set('startDate', startDate);

  let base: string;
  switch (audience) {
    case 'buyer':
      base = "/reports/buyer-invoices";
      break;
    case 'admin':
      base = "/admin-invoices";
      break;
    case 'supplier':
      base = "/finances/supplier-invoices";
      if (startDate && endDate) params.set('endDate', endDate);
      break;
  }
  return { label, path: `${base}?${params.toString()}` };
};
